import React, { useState, useEffect } from 'react';
import { NEWS_CATEGORIES } from '../../constants/contentCategories';
import { useParams, useNavigate } from 'react-router-dom';
import { useNewsServices } from '../../apis/newsService';
import { toast } from 'react-toastify';
import { Loader2, FileUp, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PublishingOptionsPanel from '../../components/admin-content-editor/PublishingOptionsPanel';

const EditNewsAdminPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getNewsById, updateNews, loading, error } = useNewsServices();

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    isTrending: false,
    isActive: true,
    commentsEnabled: false
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch news article data on component mount
  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        const response = await getNewsById(id);
        if (response.success) {
          const { title, content, category, isTrending, isActive, commentsEnabled, imageUrl } = response.data;
          setFormData({
            title,
            content,
            category,
            isTrending,
            isActive,
            commentsEnabled: Boolean(commentsEnabled)
          });
          
          if (imageUrl) {
            setCurrentImageUrl(imageUrl);
          }
        } else {
          toast.error(response.message || 'Failed to fetch news article');
          navigate('/super-admin/news-list');
        }
      } catch (err) {
        toast.error('Error fetching news article');
        navigate('/super-admin/news-list');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewsData();
  }, [id]);

  // Show error if API call fails
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle content change from Quill editor
  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: '' }));
    }
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an image
      if (!file.type.match('image.*')) {
        toast.error('Only image files are allowed');
        return;
      }
      
      // Check file size (limit to 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image file size should be less than 2MB');
        return;
      }
      
      setImageFile(file);
      
      // Create a preview URL for the image
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Remove selected image
  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await updateNews(id, formData, imageFile);
      
      if (response.success) {
        toast.success('News article updated successfully');
        navigate('/super-admin/news-list');
      } else {
        toast.error(response.message || 'Failed to update news article');
      }
    } catch (err) {
      toast.error('Error updating news article');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'align'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin mx-auto text-green-600 mb-4" />
          <p className="text-gray-600">Loading news article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Edit News Article</h1>
        
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr,17.5rem] xl:grid-cols-[1fr,19rem] gap-6 lg:gap-8 items-start">
              <div className="min-w-0 space-y-4">
            <div className="mb-4">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full p-2 border ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                placeholder="Enter news title"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`w-full p-2 border ${
                  errors.category ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500`}
              >
                <option value="">Select a category</option>
                {formData.category && !NEWS_CATEGORIES.includes(formData.category) && (
                  <option value={formData.category}>{formData.category}</option>
                )}
                {NEWS_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-500">{errors.category}</p>
              )}
            </div>
            
            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Featured Image
              </label>
              <div className="mt-1 flex items-center">
                {imagePreview ? (
                  <div className="flex flex-col items-start space-y-2">
                    <div className="relative">
                      <div className="h-40 bg-gray-200 rounded-md border border-gray-300 flex items-center justify-center">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-full object-contain rounded-md" 
                          style={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%'
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">New image selected (not yet saved)</p>
                  </div>
                ) : currentImageUrl ? (
                  <div className="flex flex-col items-start space-y-2">
                    <div className="relative">
                      <div className="h-40 bg-gray-200 rounded-md border border-gray-300 flex items-center justify-center">
                        <img 
                          src={currentImageUrl} 
                          alt="Current" 
                          className="w-full h-full object-contain rounded-md" 
                          style={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%'
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center">
                      <p className="text-sm text-gray-500 mr-2">Current image</p>
                      <label className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md cursor-pointer hover:bg-blue-700">
                        <FileUp size={14} className="mr-1" />
                        Change
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="flex w-full items-center">
                    <label className="flex flex-col items-center px-4 py-2 bg-white text-blue-600 rounded-lg border border-blue-600 cursor-pointer hover:bg-blue-50">
                      <FileUp size={18} className="mb-1" />
                      <span className="text-sm">Upload Image</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                    <span className="ml-3 text-sm text-gray-500">Max size: 2MB</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <div className={`admin-quill-wrapper border rounded-md overflow-hidden bg-white ${errors.content ? 'border-red-500' : 'border-gray-300'}`}>
                <ReactQuill
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter news content"
                />
              </div>
              {errors.content && (
                <p className="mt-1 text-sm text-red-500">{errors.content}</p>
              )}
            </div>
              </div>

              <PublishingOptionsPanel>
                <label htmlFor="isActive" className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 leading-snug">Article is active (visible on site)</span>
                </label>
                <label htmlFor="isTrending" className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="isTrending"
                    name="isTrending"
                    checked={formData.isTrending}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 leading-snug">Mark as trending news</span>
                </label>
                <label htmlFor="commentsEnabled" className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="commentsEnabled"
                    name="commentsEnabled"
                    checked={formData.commentsEnabled}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 leading-snug">Enable comments (debate / opinion)</span>
                </label>
              </PublishingOptionsPanel>
            </div>
            
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
              <button
                type="button"
                onClick={() => navigate('/super-admin/news-list')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center"
              >
                {(isSubmitting || loading) && (
                  <Loader2 size={18} className="animate-spin mr-2" />
                )}
                Update News
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditNewsAdminPage;
